import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { router } from "./router";
import { AuthProvider } from "./hooks/useAuth";
import { ToastProvider } from "./components/Toast";
import "./styles.css";
import { captureAttribution } from "./lib/attribution";

captureAttribution();

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

// No service worker => no offline, which is the whole point of the app in a
// room with bad wifi. `serviceWorker` is absent entirely on insecure origins
// (plain http:// to a LAN IP), and registration can also fail outright — both
// used to be swallowed silently, so a deployment with no offline support
// looked identical to a working one. Say so in the console instead.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js").catch((err) => {
      console.error("[trim-log] service worker registration failed — offline mode is OFF:", err);
    });
  });
} else {
  console.warn(
    "[trim-log] no serviceWorker API — offline mode is OFF. This origin is not a secure context; serve over https.",
  );
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
